import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  UserX,
  ShieldOff,
  Mail,
  MoreHorizontal,
  CheckCircle2,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@workspace/ui/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useUser } from "@/hooks/useUser";
import {
  type ReportTargetType,
  createReport,
  isReportRateLimited,
} from "@/lib/safety-api";
import { useLocalDraft } from "@/hooks/useLocalDraft";

/**
 * Shared report reasons — mirrors the mobile app's `ReportSheet`
 * (`artifacts/mobile/components/ReportSheet.tsx`) verbatim. The report
 * endpoint takes free-text `reason`, so we send the label as-is; "Something
 * else" requires a short note so vague reports still carry context.
 */
interface ReportReason {
  id: string;
  /**
   * Canonical English label sent verbatim to POST /reports as the free-text
   * `reason`. This is the moderation backend's identifier — it MUST stay in
   * English and never be localized. The user-facing label is rendered via
   * `labelKey` instead.
   */
  label: string;
  /** i18n key for the DISPLAYED reason label (localization is display-only). */
  labelKey: string;
  icon: LucideIcon;
  /** When true the details note is required before submit is enabled. */
  detailsRequired?: boolean;
}

const REPORT_REASONS: ReportReason[] = [
  { id: "inappropriate", label: "Inappropriate content", labelKey: "safety.reason_inappropriate", icon: AlertTriangle },
  { id: "harassment", label: "Harassment", labelKey: "safety.reason_harassment", icon: UserX },
  { id: "scam", label: "Scam or fraud", labelKey: "safety.reason_scam", icon: ShieldOff },
  { id: "spam", label: "Spam", labelKey: "safety.reason_spam", icon: Mail },
  { id: "other", label: "Something else", labelKey: "safety.reason_other", icon: MoreHorizontal, detailsRequired: true },
];

/**
 * Reusable report dialog — web parity with the mobile `ReportSheet`. A 2-up
 * reason grid plus an optional details note, submitting to POST /reports.
 * Success shows an inline confirmation; a 429 swaps in softer "you've
 * reported a lot" copy. The caller supplies the target (`targetType` +
 * `targetId`) and a friendly `title`; auth, submit and error handling live
 * here so entry points stay a one-liner.
 */
export function ReportDialog({
  open,
  onOpenChange,
  targetType,
  targetId,
  title,
  onReported,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetType: ReportTargetType;
  targetId: string | null;
  title?: string;
  /** Fired after a successful submit, once the reporter clicks Done. */
  onReported?: () => void;
}) {
  const { t } = useTranslation();
  const dialogTitle = title ?? t("safety.report");
  const { session } = useUser();
  const token = session?.access_token ?? null;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = REPORT_REASONS.find((r) => r.id === selectedId) ?? null;
  const detailsMissing = !!selected?.detailsRequired && details.trim().length === 0;
  // Task #7656 — validation copy only appears after a failed submit attempt,
  // never while the user is still typing. The button stays enabled so an
  // attempt is possible; `submit()` blocks and flips this flag instead.
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const disabled = busy || selectedId === null || !token;

  // Task #7654 — a report's reason + details survive an accidental close
  // (the close handler resets local state below). Keyed per target so a
  // draft about one listing/user never bleeds onto another. Cleared on
  // successful submit.
  const reportDraftValues = useMemo(() => ({ selectedId, details }), [selectedId, details]);
  const { readDraft: readReportDraft, clearDraft: clearReportDraft } = useLocalDraft({
    key: targetId ? `report:${targetType}:${targetId}` : null,
    version: 1,
    values: reportDraftValues,
    enabled: open && !done,
    isEmpty: (v) => v.selectedId === null && v.details.trim().length === 0,
  });
  const restoredForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !targetId) return;
    const restoreKey = `${targetType}:${targetId}`;
    if (restoredForRef.current === restoreKey) return;
    restoredForRef.current = restoreKey;
    const draft = readReportDraft();
    if (!draft) return;
    if (typeof draft.selectedId === "string" && REPORT_REASONS.some((r) => r.id === draft.selectedId)) {
      setSelectedId(draft.selectedId);
    }
    if (typeof draft.details === "string" && draft.details) setDetails(draft.details);
    // readReportDraft reads through a ref — stable behaviour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetType, targetId]);
  useEffect(() => {
    // Allow a fresh restore next time the dialog opens (possibly for a new target).
    if (!open) restoredForRef.current = null;
  }, [open]);

  const reset = () => {
    setSelectedId(null);
    setDetails("");
    setBusy(false);
    setDone(false);
    setError(null);
    setAttemptedSubmit(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (busy) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const submit = async () => {
    if (!token || !targetId || !selected || busy) return;
    if (detailsMissing) {
      setAttemptedSubmit(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await createReport(token, {
        target_type: targetType,
        target_id: targetId,
        reason: selected.label,
        details: details.trim() || undefined,
      });
      // Task #7654 — report delivered; drop the persisted draft.
      clearReportDraft();
      setDone(true);
    } catch (err) {
      if (isReportRateLimited(err)) {
        setError(t("safety.report_rate_limited"));
      } else {
        setError(t("common.something_went_wrong"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle>{done ? t("safety.report_sent") : dialogTitle}</DialogTitle>
          <DialogDescription>
            {done
              ? t("safety.report_sent_description")
              : t("safety.report_intro")}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 p-3 text-sm text-foreground">
              <CheckCircle2 className="h-5 w-5 shrink-0 text-brand" />
              <span>{t("safety.report_submitted")}</span>
            </div>
            <button
              type="button"
              data-testid="report-done"
              onClick={() => {
                const cb = onReported;
                reset();
                onOpenChange(false);
                cb?.();
              }}
              className="w-full rounded-lg bg-brand px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-hover"
            >
              {t("common.done")}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t("safety.report_reason")}>
              {REPORT_REASONS.map((reason) => {
                const isSelected = reason.id === selectedId;
                const Icon = reason.icon;
                return (
                  <button
                    key={reason.id}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    data-testid={`report-reason-${reason.id}`}
                    onClick={() => setSelectedId(reason.id)}
                    className={cn(
                      "flex min-h-[88px] flex-col items-center justify-center gap-2 rounded-lg border px-3 py-4 text-sm font-medium transition",
                      isSelected
                        ? "border-brand bg-brand-tint text-brand"
                        : "border-border bg-background text-muted-foreground hover:border-brand/40",
                    )}
                  >
                    <Icon className="h-5 w-5" aria-hidden="true" />
                    <span className="text-center leading-tight text-foreground">{t(reason.labelKey)}</span>
                  </button>
                );
              })}
            </div>

            <div>
              <label htmlFor="report-details" className="mb-1 block text-sm font-medium text-foreground">
                {selected?.detailsRequired ? t("safety.details_required") : t("safety.details_optional")}
              </label>
              <textarea
                id="report-details"
                data-testid="report-details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder={t("safety.details_placeholder")}
                rows={3}
                maxLength={2000}
                aria-invalid={attemptedSubmit && detailsMissing}
                aria-describedby={attemptedSubmit && detailsMissing ? "report-details-error" : undefined}
                className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none"
              />
              {attemptedSubmit && detailsMissing && (
                <p id="report-details-error" role="alert" className="mt-1 text-xs text-destructive">
                  {t("safety.details_note_required")}
                </p>
              )}
            </div>

            {error && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {error}
              </div>
            )}

            <button
              type="button"
              data-testid="report-submit"
              onClick={() => void submit()}
              disabled={disabled}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-destructive px-4 py-2.5 text-sm font-semibold text-destructive-foreground transition hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {t("safety.submit_report")}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
